import {
  useState,
  useTransition,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  startTransition,
} from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import styles from "./index.less";
import { EditorState, LexicalEditor } from "lexical";
import { nanoid } from "nanoid";
import { EditorVariablePickerType } from "../Editor/types";
import { VariableNode } from "../Editor/PromptEditor/plugins/VariablePlugin/node";
import { textToEditorState } from "../Editor/PromptEditor/utils";
import FocusPlugin from "../Editor/PromptEditor/plugins/FocusPlugin";
import VariablePickerPlugin from "../Editor/PromptEditor/plugins/VariablePickerPlugin";
import VariablePlugin from "../Editor/PromptEditor/plugins/VariablePlugin";
import OnBlurPlugin from "../Editor/PromptEditor/plugins/OnBlurPlugin";
import { SingleLinePlugin } from "../Editor/PromptEditor/plugins/SingleLinePlugin";
import { Row, Space } from "antd";
import useToken from "antd/es/theme/useToken";
import { ReactComponent as VariableIcon } from "../../../assets/variable.svg";

const DropdownMenu = ({
  variables,
  setDropdownValue,
}: {
  variables: EditorVariablePickerType[];
  setDropdownValue?: (value: string) => void;
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const nodesRef = useRef<(HTMLDivElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (event: any) => {
      if (event.keyCode === 38) {
        setHighlightedIndex((prevIndex) => {
          const index = Math.max(prevIndex - 1, 0);
          const dom = nodesRef.current[index];

          startTransition(() => {
            dom?.scrollIntoView();
          });
          return index;
        });
      } else if (event.keyCode === 40) {
        setHighlightedIndex((prevIndex) => {
          const index = Math.min(prevIndex + 1, variables.length - 1);
          const dom = nodesRef.current[index];
          startTransition(() => {
            dom?.scrollIntoView();
          });
          return index;
        });
      } else if (event.keyCode === 13 && variables[highlightedIndex]?.key) {
        setDropdownValue?.(variables[highlightedIndex].key);
      }
    },
    [highlightedIndex, variables]
  );

  const [, token] = useToken();

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!variables.length) return null;

  return (
    <div className={`nowheel ${styles["picker-container"]}`}>
      {variables.map((item, index) => {
        return (
          <Row
            className={styles["picker-item"]}
            ref={(node) => (nodesRef.current[index] = node)}
            key={item.key}
            style={{
              backgroundColor:
                highlightedIndex === index ? token.colorBgTextHover : "",
              color: highlightedIndex === index ? token.colorPrimaryActive : "",
            }}
            onMouseEnter={() => {
              setHighlightedIndex(index);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setDropdownValue?.(item.key);
            }}
          >
            <Space align="center">
              <Row align="middle">
                <VariableIcon
                  width={14}
                  height={14}
                  fill={
                    highlightedIndex === index ? token.colorPrimaryActive : ""
                  }
                />
              </Row>
              <div>
                {item.key}
                {item.key !== item.label && `(${item.label})`}
              </div>
            </Space>
          </Row>
        );
      })}
    </div>
  );
};

export default function Editor({
  h = 40,
  hasVariablePlugin = true,
  hasDropDownPlugin = false,
  variables,
  onChange,
  onBlur,
  value,
  currentValue,
  placeholder = "",
  setDropdownValue,
  updateTrigger,
}: {
  h?: number;
  hasVariablePlugin?: boolean;
  hasDropDownPlugin?: boolean;
  variables: EditorVariablePickerType[];
  onChange?: (editorState: EditorState) => void;
  onBlur?: (editor: LexicalEditor) => void;
  value?: string;
  currentValue?: string;
  placeholder?: string;
  setDropdownValue?: (value: string) => void;
  updateTrigger?: boolean;
}) {
  const [key, setKey] = useState(nanoid);
  const [, startSts] = useTransition();
  const [focus, setFocus] = useState(false);

  const initialConfig = {
    namespace: "HttpInput",
    nodes: [VariableNode],
    editorState: textToEditorState(value),
    onError: (error: Error) => {
      throw error;
    },
  };

  useEffect(() => {
    if (focus) return;
    setKey(nanoid());
  }, [value, variables.length]);

  useEffect(() => {
    setKey(nanoid());
    setFocus(false);
  }, [updateTrigger]);

  const dropdownVariables = useMemo(
    () =>
      variables.filter((item) => {
        const key = item.key.toLowerCase();
        const current = currentValue?.toLowerCase();
        return key.includes(current || "") && item.key !== currentValue;
      }),
    [currentValue, variables]
  );

  return (
    <div
      style={{ position: "relative", width: "100%", height: h, cursor: "text" }}
    >
      <LexicalComposer initialConfig={initialConfig} key={key}>
        <PlainTextPlugin
          contentEditable={
            <ContentEditable className={styles["content-editable"]} />
          }
          placeholder={<div className={styles.placeholder}>{placeholder}</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <FocusPlugin focus={focus} setFocus={setFocus} />
        <OnChangePlugin
          onChange={(e) => {
            startSts(() => {
              onChange?.(e);
            });
          }}
        />
        {hasVariablePlugin ? (
          <VariablePickerPlugin variables={variables} />
        ) : (
          ""
        )}
        <VariablePlugin variables={variables} />
        <OnBlurPlugin onBlur={onBlur} />
        <SingleLinePlugin />
        {focus && hasDropDownPlugin && (
          <DropdownMenu
            variables={dropdownVariables}
            setDropdownValue={setDropdownValue}
          />
        )}
      </LexicalComposer>
    </div>
  );
}
